import { useLocale, useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '~/components/Button';
import { useToasts } from '~/components/Toast';
import { usePushToMigratedRoute } from '~/hooks/usePushToMigratedRoute';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { usePlaygroundActions, usePlaygroundContext, usePlaygroundPreferences, usePlaygroundSelectedDestinations, usePlaygroundUIState } from '~/stores/playgroundStore';
import type { JourneyTransformed } from '~/types/Journey.types';
import { generateJourneysDefaults } from '~/types/Playground.types';
import { isDev, trackEvent } from '~/utils';
export const PlaygroundGenerateTripsButton = () => {
  const t = useTranslations();
  const locale = useLocale() as Locale;
  const generateJourneys = api.playground.generateJourneys.useMutation();
  const playgroundContext = usePlaygroundContext();
  const {
    setSkeletonTrips: setTrips,
    showDialog
  } = usePlaygroundActions();
  const selectedPlaces = usePlaygroundSelectedDestinations().cmsPlaces;
  const {
    unsupportedDestinations
  } = usePlaygroundPreferences();
  const {
    isAddingDestination,
    removingDestination
  } = usePlaygroundUIState();
  const [loading, setLoading] = useState(false);
  const pushToMigratedRoute = usePushToMigratedRoute();
  const toasts = useToasts();
  const csUser = typeof window !== 'undefined' ? sessionStorage?.getItem('csUser') ?? '' : '';
  const allowOneWayNoPlaces = process.env.NEXT_PUBLIC_FF_ENABLE_ONE_WAY_NO_PLACES_PINNED === 'true';
  const isInvalidPlaceSelection = allowOneWayNoPlaces ? !selectedPlaces.length && !playgroundContext.chosenDestination : !selectedPlaces.length;
  const shouldDisableButton = !playgroundContext || !playgroundContext.chosenDepartureDate || isInvalidPlaceSelection || unsupportedDestinations.length > 0 || isAddingDestination || removingDestination?.size > 0;
  return <Button noWrap isDisabled={shouldDisableButton} isLoading={loading} data-test-id="playground-build-trips-button" data-ua-click="playground-build-trips" onPress={async () => {
    try {
      if (!playgroundContext) return;
      setLoading(true);
      trackEvent('playground_build_trips', {
        event_category: 'playground',
        event_label: 'build trips button clicked'
      });
      const mutationData = {
        ...generateJourneysDefaults,
        internalUser: csUser,
        playgroundInputContext: playgroundContext,
        locale
      };
      const transformedTrips = (await generateJourneys.mutateAsync(mutationData)) as JourneyTransformed[];

      // ensure each trip has unique header image
      const headerImages = new Set<string>();
      transformedTrips.forEach(trip => {
        const uniqueImage = trip.header_images?.find(img => !headerImages.has(img.src));
        if (uniqueImage) {
          trip.header_image = uniqueImage;
          headerImages.add(uniqueImage.src);
        }
      });
      const failureExplanationsToShow = transformedTrips.filter(trip_0 => trip_0.items.length === 0).map(trip_1 => trip_1.failureExplanation);

      //// Enable for debugging - shows why trips failed to build

      if (isDev()) {
        failureExplanationsToShow.forEach((explanations, index) => {
          toasts.add({
            title: `Trip ${index + 1} failed`,
            description: explanations ? explanations.join(', ') : 'unknown explanation',
            variant: 'warning',
            position: 'bottom'
          });
        });
      }
      const filteredAndTransformedTrips = transformedTrips.filter(trip_2 => trip_2.items.length > 0);
      if (filteredAndTransformedTrips.length === 0) {
        throw new Error('No trips generated');
      }
      pushToMigratedRoute('/map/review');
      setTrips(filteredAndTransformedTrips);
      await new Promise(resolve => setTimeout(resolve, 300));
      setLoading(false);
    } catch (_error) {
      setLoading(false);
      showDialog('couldnt-create-trip');
    }
  }}>
      {t('playground.trip_create')}
    </Button>;
};